<script setup>
/* eslint-disable */
import { ref, computed, nextTick } from 'vue'
import { useGameStore } from '@/store/game'
import { Chocobo } from '@/models/chocobos'
import { CHOCOBO_BASE_STATS } from '@/constants/chocoboConstants'
import { getStatGrade, getStatGradeClass } from '@/utils/chocoboStats'
import { BreedingManager } from '@/services/breedingManager'
import ranchHandImage from '@/assets/ranch_hand.jpg'
import { GameActions, GameActionTypes } from '@/utils/gameActions'
import { BreedingPoolManager } from '@/services/breedingPoolManager'
import BreedingOverlay from './BreedingOverlay.vue'
import { getChocoboColorClass } from '@/utils/colorUtils'
import { applyThemeClasses } from '@/utils/themeColors'


const BREEDING_COST = 30000
const BASE_RENTAL_MIN = 500
const BASE_RENTAL_MAX = 35000

const props = defineProps({
  selectedChocobo: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['close'])
const gameStore = useGameStore()
const showRental = ref(false)
const selectedPartner = ref(null)

const calculateRentalCost = (chocobo) => {
  let qualityScore = 0
  
  const speedGrade = getStatGrade(chocobo.speed)
  const staminaGrade = getStatGrade(chocobo.stamina)
  
  const gradeValues = {
    'S+': 20, 'S': 18, 'S-': 16,
    'A+': 14, 'A': 12, 'A-': 10,
    'B+': 8, 'B': 6, 'B-': 4,
    'C+': 3, 'C': 2, 'C-': 1,
    'D+': 0, 'D': 0, 'D-': 0,
    'E': 0, 'F': 0
  }
  
  qualityScore += gradeValues[speedGrade] || 0
  qualityScore += gradeValues[staminaGrade] || 0
  
  const colorScores = {
    'gold': 20,
    'black': 12,
    'blue': 8,
    'green': 8,
    'pink': 8,
    'yellow': 1
  }
  qualityScore += colorScores[chocobo.color] || 5
  
  qualityScore += Math.min(20, chocobo.level / 2)
  qualityScore += Math.max(0, 10 - chocobo.behaviorId)
  
  if (chocobo.hasSpeedXFactor) qualityScore += 5
  if (chocobo.hasStaminaXFactor) qualityScore += 5
  
  const costMultiplier = qualityScore / 100
  const rentalCost = Math.round(
    BASE_RENTAL_MIN + (BASE_RENTAL_MAX - BASE_RENTAL_MIN) * costMultiplier
  )
  
  return rentalCost
}

const eligiblePartners = computed(() => {
  return gameStore.playerChocobos.filter(chocobo => 
    chocobo.id !== props.selectedChocobo.id &&
    chocobo.level >= 5 &&
    chocobo.gender !== props.selectedChocobo.gender
  )
})

const retiredPartners = computed(() => {
  const retired = gameStore.breedingPoolPartners(props.selectedChocobo)
  const playerMoney = gameStore.playerOwner.money
  const maxAffordableBreeding = playerMoney - BREEDING_COST
  
  // Filter by affordability
  return retired.filter(partner => partner.breedingPrice <= maxAffordableBreeding)
})

const rentalPartners = computed(() => {
  // If we have retired partners, prioritize them
  if (retiredPartners.value.length > 0) {
    return retiredPartners.value
  }
  
  // Fallback to old rental system if no retired partners available
  const rentals = []
  const playerMoney = gameStore.playerOwner.money
  const maxAffordableRental = playerMoney - BREEDING_COST
  
  if (maxAffordableRental < BASE_RENTAL_MIN) {
    return []
  }
  
  const validCombinations = []
  for (const [color, regions] of Object.entries(CHOCOBO_BASE_STATS)) {
    for (const region of Object.keys(regions)) {
      validCombinations.push({ color, region })
    }
  }
  
  const attempts = Math.min(15, validCombinations.length)
  for (let i = 0; i < attempts; i++) {
    const { color, region } = validCombinations[
      Math.floor(Math.random() * validCombinations.length)
    ]
    
    const level = Math.floor(Math.random() * 11) + 5
    const gender = props.selectedChocobo.gender === 'male' ? 'female' : 'male'
    
    const rental = new Chocobo(color, region, `Rental ${gender === 'male' ? 'Rooster' : 'Hen'} ${rentals.length + 1}`)
    rental.level = level
    rental.gender = gender
    rental.isRental = true
    
    const baseStats = CHOCOBO_BASE_STATS[color][region]
    rental.speed = baseStats.base_speed + Math.floor(Math.random() * 15)
    rental.stamina = baseStats.base_stamina + Math.floor(Math.random() * 15)
    
    rental.hasSpeedXFactor = Math.random() < (baseStats.xfactor_percentage / 100)
    rental.hasStaminaXFactor = Math.random() < (baseStats.xfactor_percentage / 100)
    
    rental.rentalCost = calculateRentalCost(rental)
    
    if (rental.rentalCost <= maxAffordableRental) {
      rentals.push(rental)
    }
    
    if (rentals.length >= 5) break
  }
  
  return rentals
})

const getTotalCost = () => {
  if (!selectedPartner.value) return BREEDING_COST
  
  let partnerCost = 0
  if (selectedPartner.value.isRetiredBreeder) {
    partnerCost = selectedPartner.value.breedingPrice
  } else if (selectedPartner.value.isRental) {
    partnerCost = selectedPartner.value.rentalCost
  }
  
  return BREEDING_COST + partnerCost
}

const canAffordBreeding = computed(() => {
  if (!selectedPartner.value) return false
  return gameStore.playerOwner.money >= getTotalCost()
})

const showLoading = ref(false)

const breed = async () => {
  
  if (!selectedPartner.value || !canAffordBreeding.value) return
  showLoading.value = true
  await nextTick()
  await new Promise(resolve => setTimeout(resolve, 10))
  try {
    // Calculate total cost
    let partnerCost = 0
    if (selectedPartner.value.isRetiredBreeder) {
      partnerCost = selectedPartner.value.breedingPrice
    } else if (selectedPartner.value.isRental) {
      partnerCost = selectedPartner.value.rentalCost
    }
    
    const totalCost = BREEDING_COST + partnerCost

    // Handle retired breeder transaction
    if (selectedPartner.value.isRetiredBreeder) {
      const breedingResult = BreedingPoolManager.processBreeding(
        selectedPartner.value, 
        props.selectedChocobo, 
        gameStore
      )
      
      if (!breedingResult?.success) {
        console.error('Breeding transaction failed:', breedingResult?.error)
        return
      }
    } else {
      // Pay costs for regular/rental breeding
      gameStore.playerOwner.money -= totalCost
    }
    
    // Perform breeding
    const mother = props.selectedChocobo.gender === 'female' ? props.selectedChocobo : selectedPartner.value
    const father = props.selectedChocobo.gender === 'male' ? props.selectedChocobo : selectedPartner.value
    
    // Start the 14-day breeding process
    const breedingRecord = BreedingManager.startBreeding(mother, father, gameStore)
    
    // Advance the game day
    await GameActions.performAction(GameActionTypes.BREEDING)
    
    // Close modal
    emit('close')

  } catch (error) {
    console.error('Breeding failed:', error)
  } finally {
    showLoading.value = false
  }
}

const getRanchHandAdvice = computed(() => {
  if (!showRental.value) {
    if (eligiblePartners.value.length === 0) {
      return `We have no birds in the stable that are compatible with ${props.selectedChocobo.name}.`
    }
    return `These are the only birds we have in the stable that are compatible with ${props.selectedChocobo.name}.`
  }

  if (rentalPartners.value.length === 0) {
    return `We can't afford to rent any breeding partners for ${props.selectedChocobo.name} right now. We'd need at least ${(BASE_RENTAL_MIN + BREEDING_COST).toLocaleString()}G to get started.`
  }

  const maxPossibleRental = gameStore.playerOwner.money - BREEDING_COST
  const canAffordBest = maxPossibleRental >= BASE_RENTAL_MAX
  const partnerType = props.selectedChocobo.gender === 'male' ? 'hen' : 'stud'
  const partnerTypePlural = props.selectedChocobo.gender === 'male' ? 'hens' : 'studs'

  const avgQualityScore = rentalPartners.value.reduce((sum, rental) => {
    const score = (rental.rentalCost - BASE_RENTAL_MIN) / (BASE_RENTAL_MAX - BASE_RENTAL_MIN)
    return sum + score
  }, 0) / rentalPartners.value.length

  if (canAffordBest && avgQualityScore < 0.8) {
    return `This is what's available at the moment for ${props.selectedChocobo.name}. We can probably pay for the best ${partnerType}, so it might be worth waiting until the very best ${partnerTypePlural} are available to us.`
  }

  if (avgQualityScore > 0.8) {
    return `This is la crème de la crème of the options for ${props.selectedChocobo.name}. These are the best ${partnerTypePlural} available in the world right now.`
  }

  const percentOfMax = maxPossibleRental / BASE_RENTAL_MAX
  if (percentOfMax > 0.7) {
    return `These are the birds we can afford to rent for breeding with ${props.selectedChocobo.name}. We're not far off being able to afford the best ${partnerType} though, maybe we can check in again after a few more races?`
  }

  return `These are the birds we can afford to rent for breeding with ${props.selectedChocobo.name}. There's better partners out there, but the ranches are charging more than we can afford right now.`
})

// Breeding compatibility calculator
const getBreedingPotential = (partner) => {
  const mother = props.selectedChocobo.gender === 'female' ? props.selectedChocobo : partner
  const father = props.selectedChocobo.gender === 'male' ? props.selectedChocobo : partner
  
  let score = 0
  let maxScore = 0
  
  // Speed potential (30% weight)
  const avgSpeed = (mother.speed + father.speed) / 2
  score += Math.min(avgSpeed / 120, 1) * 30
  maxScore += 30
  
  // Stamina potential (30% weight)
  const avgStamina = (mother.stamina + father.stamina) / 2
  score += Math.min(avgStamina / 120, 1) * 30
  maxScore += 30
  
  // Level bonus (20% weight)
  const avgLevel = (mother.level + father.level) / 2
  score += Math.min(avgLevel / 40, 1) * 20
  maxScore += 20
  
  // X-Factor bonus (20% weight)
  let xFactorBonus = 0
  if (mother.hasSpeedXFactor || father.hasSpeedXFactor) xFactorBonus += 10
  if (mother.hasStaminaXFactor || father.hasStaminaXFactor) xFactorBonus += 10
  score += xFactorBonus
  maxScore += 20
  
  const percentage = (score / maxScore) * 100
  
  if (percentage >= 80) return { grade: 'S', color: 'text-purple-600', bgColor: 'bg-purple-100' }
  if (percentage >= 70) return { grade: 'A', color: 'text-blue-600', bgColor: 'bg-blue-100' }
  if (percentage >= 60) return { grade: 'B', color: 'text-green-600', bgColor: 'bg-green-100' }
  if (percentage >= 50) return { grade: 'C', color: 'text-yellow-600', bgColor: 'bg-yellow-100' }
  return { grade: 'D', color: 'text-red-600', bgColor: 'bg-red-100' }
}

const getColorCombinationHint = (partner) => {
  const mother = props.selectedChocobo.gender === 'female' ? props.selectedChocobo : partner
  const father = props.selectedChocobo.gender === 'male' ? props.selectedChocobo : partner
  
  // Special combinations
  if ((mother.color === 'black' && father.color === 'yellow') || 
      (mother.color === 'yellow' && father.color === 'black')) {
    return { text: 'Golden chance!', color: 'text-yellow-600' }
  }
  
  if (mother.color === father.color) {
    return { text: 'Pure breeding', color: 'text-blue-600' }
  }
  
  return { text: 'Color blend', color: 'text-gray-600' }
}

</script>

<template>
  <div :class="`fixed inset-0 ${applyThemeClasses('components.modal.overlay')} flex items-center justify-center z-50`">
    <div :class="`rounded-lg ${applyThemeClasses('components.modal.container')} max-h-[90vh] overflow-y-auto p-6`">
      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <h2 :class="`${applyThemeClasses('text.primary')}`">Chocobo Breeding</h2>
        <button 
          @click="$emit('close')"
          class="text-gray-500 hover:text-gray-700"
        >
          ✕
        </button>
      </div>

      <!-- Selected Chocobo Info -->
      <div :class="`${applyThemeClasses('race.advice.container')} p-4`">
        <div class="flex items-start space-x-4">
          <img 
            :src="ranchHandImage" 
            alt="Ranch Hand" 
            class="w-16 h-16 rounded-lg object-cover"
          />
          <div class="flex-1">
            <p :class="applyThemeClasses('race.advice.text')">
              "{{ getRanchHandAdvice }}"
            </p>
          </div>
        </div>
      </div>
      <div class="mb-6">
        <h3 :class="`${applyThemeClasses('text.primaryLow')}`">Selected Parent</h3>
        <div class="border rounded-lg p-4">
          <div class="flex items-center space-x-4">
            <div 
              :class="[getChocoboColorClass(selectedChocobo.color), 'w-16 h-16 rounded-lg']"
            ></div>
            <div>
              <h4 :class="`${applyThemeClasses('text.secondaryBold')}`">{{ selectedChocobo.name }}</h4>
              <p :class="`${applyThemeClasses('text.secondary')} text-sm`">
                Level {{ selectedChocobo.level }} {{ selectedChocobo.color.charAt(0).toUpperCase() + selectedChocobo.color.slice(1) }}
                ({{ selectedChocobo.gender.charAt(0).toUpperCase() + selectedChocobo.gender.slice(1) }})
              </p>
              <div class="mt-1 space-x-2">
                <span 
                  :class="[
                    getStatGradeClass(getStatGrade(selectedChocobo.speed)),
                    'px-2 py-1 rounded text-sm font-medium'
                  ]"
                >
                  Speed: {{ getStatGrade(selectedChocobo.speed) }}
                </span>
                <span 
                  :class="[
                    getStatGradeClass(getStatGrade(selectedChocobo.stamina)),
                    'px-2 py-1 rounded text-sm font-medium'
                  ]"
                >
                  Stamina: {{ getStatGrade(selectedChocobo.stamina) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Breeding Partner Selection -->
      <div class="mb-6">
        <div class="flex justify-between items-center mb-4">
          <h3 :class="`${applyThemeClasses('text.primaryLow')}`">Select Breeding Partner</h3>
          <button 
            @click="showRental = !showRental"
            class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            {{ showRental ? 'Show Your Chocobos' : 'Show Breeding Pool' }}
          </button>
        </div>

        <!-- Partner Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <template v-if="!showRental">
            <div v-if="eligiblePartners.length === 0" :class="`${applyThemeClasses('text.secondaryBold')}`">
              No eligible breeding partners in your stable
            </div>
            
            <div 
              v-for="partner in eligiblePartners"
              :key="partner.id"
              @click="selectedPartner = partner"
              :class="[
                'border rounded-lg p-4 cursor-pointer transition-colors relative',
                selectedPartner?.id === partner.id ? 'border-blue-500 bg-blue-500 bg-opacity-10' : 'hover:bg-gray-50'
              ]"
            >
              <div class="flex items-center space-x-4">
                <div 
                  :class="[getChocoboColorClass(partner.color), 'w-12 h-12 rounded-lg']"
                ></div>
                <div>
                  <h4 :class="`${applyThemeClasses('text.secondaryBold')}`">{{ partner.name }}</h4>
                  <p :class="`${applyThemeClasses('text.secondary')}`">
                    Level {{ partner.level }} {{ partner.color.charAt(0).toUpperCase() + partner.color.slice(1) }}
                  </p>
                  <div class="mt-1 space-x-2">
                    <span 
                      :class="[
                        getStatGradeClass(getStatGrade(partner.speed)),
                        'px-2 py-1 rounded text-sm font-medium'
                      ]"
                    >
                      Speed: {{ getStatGrade(partner.speed) }}
                    </span>
                    <span 
                      :class="[
                        getStatGradeClass(getStatGrade(partner.stamina)),
                        'px-2 py-1 rounded text-sm font-medium'
                      ]"
                    >
                      Stamina: {{ getStatGrade(partner.stamina) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <template v-else>
            <div v-if="rentalPartners.length === 0" :class="`${applyThemeClasses('text.secondaryBold')}`">
              No affordable rental options available
              <p :class="`${applyThemeClasses('text.money')}`">Available Funds: {{ gameStore.playerOwner.money.toLocaleString() }}G</p>
              <p :class="`${applyThemeClasses('text.secondaryBold')}`">Breeding Cost: {{ BREEDING_COST.toLocaleString() }}G</p>
            </div>
            
            <div 
              v-for="rental in rentalPartners"
              :key="rental.id"
              @click="selectedPartner = rental"
              :class="[
                'border rounded-lg p-4 cursor-pointer transition-colors relative',
                selectedPartner?.id === rental.id ? 'border-blue-500 bg-blue-500 bg-opacity-10' : 'hover:bg-gray-50'
              ]"
            >
              <div class="flex items-center space-x-4">
                <div 
                  :class="[getChocoboColorClass(rental.color), 'w-12 h-12 rounded-lg']"
                ></div>
                <div>
                  <h4 :class="`${applyThemeClasses('text.secondaryBold')}`">{{ rental.name }}</h4>
                  <p :class="`${applyThemeClasses('text.secondary')} text-sm`">
                    Level {{ rental.level }} {{ rental.color.charAt(0).toUpperCase() + rental.color.slice(1) }}
                    <span v-if="rental.isRetiredBreeder" class="text-gray-500">
                      (Retired Chocobo)
                    </span>
                  </p>
                  <div class="mt-1 space-x-2">
                    <span 
                      :class="[
                        getStatGradeClass(rental.gradeSpeed || getStatGrade(rental.speed)),
                        'px-2 py-1 rounded text-sm font-medium'
                      ]"
                    >
                      Speed: {{ rental.gradeSpeed || getStatGrade(rental.speed) }}
                    </span>
                    <span 
                      :class="[
                        getStatGradeClass(rental.gradeStamina || getStatGrade(rental.stamina)),
                        'px-2 py-1 rounded text-sm font-medium'
                      ]"
                    >
                      Stamina: {{ rental.gradeStamina || getStatGrade(rental.stamina) }}
                    </span>
                  </div>
                  
                  <!-- Career Stats for Retired Breeders -->
                  <div v-if="rental.isRetiredBreeder && rental.hasRaceHistory" class="mt-2 text-xs text-gray-600">
                    <div class="flex space-x-4">
                      <span>{{ rental.wins }}/{{ rental.totalRaces }} wins ({{ rental.winRate }}%)</span>
                      <span>Avg: {{ rental.avgPosition }}</span>
                      <span>Earnings: {{ rental.careerEarnings.toLocaleString() }}G</span>
                    </div>
                  </div>
                  
                  <!-- Transit Chocobo Info -->
                  <div v-else-if="rental.isRetiredBreeder && !rental.hasRaceHistory" class="mt-2 text-xs text-gray-500">
                    <span>Never raced - used for transit and breeding</span>
                  </div>
                  
                  <div class="mt-2">
                    <span class="text-sm font-medium text-green-600">
                      {{ rental.isRetiredBreeder ? 'Breeding Fee' : 'Rental Cost' }}: 
                      {{ (rental.breedingPrice || rental.rentalCost).toLocaleString() }}G
                    </span>
                    <div class="text-xs text-gray-500">
                      <span v-if="rental.hasSpeedXFactor" class="mr-2">Speed X-Factor</span>
                      <span v-if="rental.hasStaminaXFactor">Stamina X-Factor</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Breeding Cost and Action -->
      <div class="mt-6 border-t pt-6">
        <div class="flex justify-between items-center">
          <div>
            <p class="text-lg font-semibold">
              Total Cost: {{ getTotalCost().toLocaleString() }}G
            </p>
            <p :class="`${applyThemeClasses('text.secondary')}`">
              Breeding Fee: {{ BREEDING_COST.toLocaleString() }}G
              <span v-if="selectedPartner?.isRetiredBreeder">
                + Breeding Fee: {{ selectedPartner.breedingPrice.toLocaleString() }}G
              </span>
              <span v-else-if="selectedPartner?.isRental">
                + Rental Fee: {{ selectedPartner.rentalCost.toLocaleString() }}G
              </span>
            </p>
            <p :class="`${applyThemeClasses('text.money')}`">
              Available Funds: {{ gameStore.playerOwner.money.toLocaleString() }}G
            </p>
          </div>
          <button 
            @click="breed"
            :disabled="!selectedPartner || !canAffordBreeding"
            :class="[
              'px-6 py-2 rounded-lg text-white',
              canAffordBreeding && selectedPartner
                ? 'bg-green-500 hover:bg-green-600'
                : 'bg-gray-400 cursor-not-allowed'
            ]"
          >
            Breed Chocobos
          </button>
        </div>
      </div>
      <BreedingOverlay v-if="showLoading" />
    </div>
  </div>
</template>